import React, { useState } from 'react';
import { Share2, Copy, Check, Twitter, Linkedin } from 'lucide-react';
import FeatureAnnouncement from '@/components/FeatureAnnouncement';
import SEO from '@/components/SEO';

const Announcement: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://dombase.xyz/announcement';
  const shareTitle = 'DomBase Updates — Watchlist, Scan Progress & Brand Risk';
  const shareDescription = 'DomBase just shipped a domain watchlist with auto re-check, live scan progress bar, brand risk front and center, and a fully rewritten Keywords table.';

  const handleShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareDescription, url: shareUrl });
        return;
      } catch (err) {
        if ((err as Error).name !== 'AbortError') handleCopy();
        return;
      }
    }
    handleCopy();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  const shareToTwitter = () =>
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');

  const shareToLinkedIn = () =>
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');

  return (
    <>
      <SEO
        title="DomBase Updates — Watchlist, Scan Progress & Brand Risk"
        description="DomBase just shipped a domain watchlist with auto re-check, live scan progress bar, brand risk front and center, and a fully rewritten Keywords table."
        canonical="/announcement"
        ogTitle="DomBase Updates — Watchlist, Scan Progress & Brand Risk"
        ogDescription="DomBase just shipped a domain watchlist with auto re-check, live scan progress bar, brand risk front and center, and a fully rewritten Keywords table."
        ogUrl="https://dombase.xyz/announcement"
        ogImage="https://dombase.xyz/og.png"
        ogType="article"
        twitterTitle="DomBase Updates — Watchlist, Scan Progress & Brand Risk"
        twitterDescription="Domain watchlist, live scan progress, brand risk front and center, and a rewritten Keywords table — all shipped."
        twitterCard="summary_large_image"
        twitterImage="https://dombase.xyz/og.png"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'DomBase Updates — Watchlist, Scan Progress & Brand Risk Sprint',
          description: 'Domain watchlist with auto re-check, live scan progress bar, brand risk front and center, and rewritten Keywords table.',
          image: 'https://dombase.xyz/og.png',
          datePublished: '2026-05-26',
          dateModified: '2026-06-09',
          author: { '@type': 'Organization', name: 'DomBase' },
          publisher: {
            '@type': 'Organization',
            name: 'DomBase',
            logo: { '@type': 'ImageObject', url: 'https://dombase.xyz/logo.png' },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://dombase.xyz/announcement' },
        }}
      />

      <div className="min-h-screen bg-white">

        {/* Page header — blue color block */}
        <div className="relative bg-blue-500 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-white/5 rounded-full pointer-events-none" />
          <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-white/5 rounded-full pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-2">
              What's New
            </h1>
            <p className="text-blue-100 font-medium text-lg">
              Updates, upcoming features, and the DomBase changelog.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

          {/* Share bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-12 pb-6 border-b-2 border-gray-100">
            <p className="text-sm text-gray-400 font-medium">Share these updates</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white font-semibold text-sm rounded-md transition-all duration-200 hover:bg-blue-600 hover:scale-105"
              >
                <Share2 className="h-4 w-4" />
                Share
              </button>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-semibold text-sm rounded-md transition-all duration-200 hover:bg-gray-200 hover:scale-105"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                onClick={shareToTwitter}
                className="p-2 rounded-md bg-sky-500 text-white hover:bg-sky-600 transition-all duration-200 hover:scale-105"
                aria-label="Share on X / Twitter"
              >
                <Twitter className="h-4 w-4" />
              </button>
              <button
                onClick={shareToLinkedIn}
                className="p-2 rounded-md bg-blue-700 text-white hover:bg-blue-800 transition-all duration-200 hover:scale-105"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </button>
            </div>
          </div>

          <FeatureAnnouncement variant="page" />
        </div>
      </div>
    </>
  );
};

export default Announcement;
